import { useState, useEffect, useRef } from 'preact/hooks';
import { authFetch } from '../api/authFetch';

export type EffortLevel = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

export interface ModelCapabilities {
  supportsEffort?: boolean;
  supportedEffortLevels?: EffortLevel[];
  supportsAdaptiveThinking?: boolean;
  supports1MContext?: boolean;
  /** Local model exposes native function-calling (e.g. the Qwen coding agent). */
  toolCapable?: boolean;
  /** Local · private · zero API cost. */
  local?: boolean;
  /** Only surfaced to admins (filtered server-side in GET /models). */
  adminOnly?: boolean;
}

export interface RegistryModel {
  modelId: string;
  displayName: string;
  // 'kimi' = Kimi K3 (Moonshot), routed via KimiAgentService — server-config/plans/
  // llm-provider-interop-kimi-k3.md §1.3/§4. Gated server-side by kimi_enabled ∧
  // MOONSHOT_API_KEY (model-registry.service.ts seedKimi/isAvailable) — the row simply
  // doesn't appear in GET /api/models when dark, so no frontend gate is needed here.
  provider: 'anthropic' | 'google' | 'ollama' | 'qwen' | 'local-5090' | 'kimi';
  family: string | null;
  tier: 'fast' | 'balanced' | 'powerful' | null;
  maxInputTokens: number | null;
  maxOutputTokens: number | null;
  isDefault: boolean;
  sortOrder: number;
  capabilities?: ModelCapabilities;
}

/** Group models by provider */
export interface ModelsByProvider {
  anthropic: RegistryModel[];
  google: RegistryModel[];
  ollama: RegistryModel[];
  qwen: RegistryModel[];
  'local-5090': RegistryModel[];
  kimi: RegistryModel[];
}

// Module-level cache — shared across all component instances, survives re-renders
let cachedModels: RegistryModel[] | null = null;
let fetchPromise: Promise<RegistryModel[]> | null = null;

// Listeners notified when cache is invalidated (e.g. MODEL_CATALOG_UPDATED WebSocket event)
const invalidationListeners = new Set<() => void>();

function fetchModels(signal?: AbortSignal): Promise<RegistryModel[]> {
  // Only a NON-EMPTY catalog counts as a valid cache. An empty array (transient /api/models
  // failure, auth-token race on first load, or a non-ok response) must NEVER be cached as if
  // it were the real catalog — `if (cachedModels)` would otherwise treat `[]` as loaded and
  // never retry, silently stripping every capability-gated control (effort ruler + 1M toggle)
  // for the whole page load. See the ContextBar tuning-vanish bug (2026-06-04).
  if (cachedModels && cachedModels.length) return Promise.resolve(cachedModels);
  if (fetchPromise) return fetchPromise;
  fetchPromise = authFetch('/api/models', { signal })
    .then((r) => (r.ok ? r.json() : []))
    .then((data: RegistryModel[]) => {
      if (Array.isArray(data) && data.length) cachedModels = data;
      fetchPromise = null;
      return cachedModels ?? [];
    })
    .catch(() => {
      fetchPromise = null;
      return cachedModels ?? ([] as RegistryModel[]);
    });
  return fetchPromise;
}

/**
 * Invalidate the cache and notify all mounted useModels() hooks to re-fetch.
 * Called when MODEL_CATALOG_UPDATED WebSocket event is received.
 */
export function invalidateModelCache() {
  cachedModels = null;
  fetchPromise = null;
  invalidationListeners.forEach((cb) => cb());
}

/**
 * Hook that fetches available models from the registry API.
 * Caches at module level so the API is called at most once per page load.
 * Automatically re-fetches when the server emits MODEL_CATALOG_UPDATED.
 */
export function useModels() {
  const [models, setModels] = useState<RegistryModel[]>(cachedModels ?? []);
  const [loading, setLoading] = useState(!(cachedModels && cachedModels.length));
  const retriesRef = useRef(0);

  useEffect(() => {
    let ac = new AbortController();
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    function load() {
      if (cachedModels && cachedModels.length) {
        setModels(cachedModels);
        setLoading(false);
        return;
      }
      ac = new AbortController();
      setLoading(true);
      fetchModels(ac.signal).then((data) => {
        if (ac.signal.aborted) return;
        setModels(data);
        setLoading(false);
        // Self-heal: an empty catalog strips every capability-gated control (effort ruler +
        // 1M toggle). A one-shot /api/models failure or auth race must not leave the UI
        // permanently stripped for the page load — retry a few times with backoff until the
        // catalog populates. A non-empty result resets the counter.
        if (!data.length && retriesRef.current < 3) {
          const delay = 1000 * Math.pow(2, retriesRef.current); // 1s, 2s, 4s
          retriesRef.current += 1;
          retryTimer = setTimeout(load, delay);
        } else if (data.length) {
          retriesRef.current = 0;
        }
      });
    }

    load();
    invalidationListeners.add(load);
    return () => {
      ac.abort();
      if (retryTimer) clearTimeout(retryTimer);
      invalidationListeners.delete(load);
    };
  }, []);

  const byProvider: ModelsByProvider = {
    anthropic: models.filter((m) => m.provider === 'anthropic'),
    google: models.filter((m) => m.provider === 'google'),
    ollama: models.filter((m) => m.provider === 'ollama'),
    qwen: models.filter((m) => m.provider === 'qwen'),
    'local-5090': models.filter((m) => m.provider === 'local-5090'),
    kimi: models.filter((m) => m.provider === 'kimi'),
  };

  return { models, byProvider, loading };
}
